"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import BlockWrapper from "@/ui/common/BlockWrapper"
import styled from "@emotion/styled"
import { Box, CircularProgress, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import { useState } from "react"
import ButtonWithArrow from "ui/common/bit/ButtonWithArrow"
import ExtendedPlusMinusCollapse from "ui/common/bit/ExtendedPlusMinusCollapse"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { useJobsContext } from "../../JobsContext"
import { DrushimFormDialog } from "./DrushimFormDialog"

const StyledBlockWrapper = styled(BlockWrapper)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const ResultsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 944px;
  gap: ${sizes.baseQuadric};
`

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`

const ErrorMessage = styled(Typography)`
  color: ${colors.red};
  text-align: center;
  margin: ${sizes.baseDouble} 0;
`

const EmptyResults = styled(Typography)`
  text-align: center;
  margin: ${sizes.baseDouble} 0;
  color: ${colors.gray};
`

const JobInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
`

const JobTitle = styled(Typography)`
  display: flex;
  flex-direction: row;
  font-weight: 600;
  color: ${colors.red};
  font-size: 19px;
  gap: 20px;
  ${mediaQueries.desktopM} {
    font-size: 18px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 22px;
  }
`
const JobTitleBlack = styled(JobTitle)`
  color: ${colors.black};
`

const JobDetailsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${sizes.baseDouble};
`

const JobDetail = styled(Box)`
  color: ${colors.black};
  font-size: 16px;
  display: flex;
  align-items: center;
  &:not(:last-child) {
    padding-inline-end: ${sizes.baseDouble};
    border-inline-end: solid 1px ${colors.borderGray};
  }
`

const StyledButtonWithArrow = styled(ButtonWithArrow)`
  padding: 10px 18px 10px 14px;
  ${mediaQueries.desktopM} {
    padding: 10px 18px 10px 14px;
  }
  margin-top: 40px;
`

const JobFullDescription = styled(Box)`
  color: ${colors.black};
  font-size: 15px;
  margin-top: ${sizes.baseSingle};
  white-space: pre-wrap;
`

const JobResults = ({ data }: any) => {
  const { buttonText, titleDescription, titleRequirements } = data
  const { filteredJobs, isLoading, error, searchQuery } = useJobsContext()
  const t = useTranslations("JobsResults")
  const PlusIcon = iconsMap["plus-element"]
  const MinusIcon = iconsMap["minus-element"]
  const [open, setOpen] = useState(false)
  const [jobId, setJobId] = useState("")
  const [jobTitle, setJobTitle] = useState("")

  const handleViewJob = (jobId: string, jobTitle: string) => {
    setOpen(true)
    setJobId(jobId)
    setJobTitle(jobTitle)
  }

  const handleClose = () => {
    setOpen(false)
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress size={40} />
      </LoadingContainer>
    )
  }

  if (error) {
    return <ErrorMessage variant="body1">{error}</ErrorMessage>
  }

  if (filteredJobs.length === 0 && searchQuery) {
    return <EmptyResults variant="body1">{t("noMatchingJobs")}</EmptyResults>
  }

  if (filteredJobs.length === 0) {
    return <EmptyResults variant="body1">{t("noAvailableJobs")}</EmptyResults>
  }

  return (
    <StyledBlockWrapper>
      <DrushimFormDialog
        open={open}
        handleClose={handleClose}
        jobId={jobId}
        jobTitle={jobTitle}
        data={data}
      ></DrushimFormDialog>
      <ResultsContainer>
        {filteredJobs.map((job) => {
          const {
            jobName,
            description,
            requirements,
            scopeJobName,
            areaName,
            professionName,
          } = job

          const summaryContent = (
            <JobInfo>
              <JobTitle variant="h6">
                {jobName}
                {professionName && professionName !== "Default" && (
                  <JobDetail>{professionName}</JobDetail>
                )}
              </JobTitle>
              <JobDetailsContainer>
                {areaName && <JobDetail>{areaName}</JobDetail>}
                {scopeJobName && <JobDetail>{scopeJobName}</JobDetail>}
              </JobDetailsContainer>
            </JobInfo>
          )
          const detailsContent = (
            <>
              {description && (
                <>
                  <JobTitleBlack variant="h6">
                    {titleDescription?.value}
                  </JobTitleBlack>
                  <JobFullDescription>{description}</JobFullDescription>
                </>
              )}
              {requirements && (
                <>
                  <JobTitleBlack variant="h6">
                    {titleRequirements?.value}
                  </JobTitleBlack>
                  <JobFullDescription>{requirements}</JobFullDescription>
                </>
              )}
              <StyledButtonWithArrow
                onClick={() => {
                  job.orderId !== undefined &&
                    handleViewJob(job.orderId.toString(), job?.jobName || "")
                }}
              >
                {buttonText?.value || t("moreInfo")}
              </StyledButtonWithArrow>
            </>
          )

          return (
            <ExtendedPlusMinusCollapse
              key={job.id}
              openIcon={<PlusIcon />}
              closeIcon={<MinusIcon />}
              summaryContent={summaryContent}
              detailsContent={detailsContent}
              onClick={() => {
                // You can add analytics tracking here if needed
              }}
            />
          )
        })}
      </ResultsContainer>
    </StyledBlockWrapper>
  )
}

export default JobResults
